import { useEffect, useState } from 'react';
import api from '../api/apiClient';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import useAuth from '../hooks/useAuth';

const emptyTemplate = { title: '', category: 'cognitive', description: '', age_group: '' };

const CATEGORIES = [
  { id: 'cognitive', label: 'Cognitive' },
  { id: 'emotional', label: 'Emotional' },
  { id: 'social', label: 'Social' },
  { id: 'motor', label: 'Motor' },
];

const TERMS = [
  { id: 'T1', label: 'Term 1' },
  { id: 'T2', label: 'Term 2' },
  { id: 'T3', label: 'Term 3' },
];

export default function TrackerPage() {
  const { user, currentBranch } = useAuth();
  const [overview, setOverview] = useState({ scores: [], templates: [] });
  const [branches, setBranches] = useState([]);
  const [template, setTemplate] = useState(emptyTemplate);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // ─── Development Tracker Modal State ───
  const [isTrackerModalOpen, setIsTrackerModalOpen] = useState(false);
  const [trackerBranchId, setTrackerBranchId] = useState('');
  const [trackerStudentId, setTrackerStudentId] = useState('');
  const [branchStudents, setBranchStudents] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState('T1');
  const [selectedCategory, setSelectedCategory] = useState('cognitive');
  const [allSkills, setAllSkills] = useState([]); // raw skills from backend
  const [termScores, setTermScores] = useState({}); // term scores by category from backend
  const [milestones, setMilestones] = useState([]); // active category skills
  const [progressNotes, setProgressNotes] = useState('');
  const [loadingStudentTracker, setLoadingStudentTracker] = useState(false);
  const [savingTracker, setSavingTracker] = useState(false);

  // Sub-modal for Add New Milestone
  const [isAddMilestoneOpen, setIsAddMilestoneOpen] = useState(false);
  const [newMilestoneTitle, setNewMilestoneTitle] = useState('');
  const [newMilestonePercentage, setNewMilestonePercentage] = useState(50);

  useEffect(() => {
    loadOverview();
    if (user?.role === 'admin') {
      loadBranches();
    } else if (user?.role === 'center_manager') {
      const bId = currentBranch?.branch_id ? String(currentBranch.branch_id) : '';
      setTrackerBranchId(bId);
      if (bId) loadStudentsForBranch(bId);
    }
  }, [currentBranch]);

  async function loadOverview() {
    try {
      const response = await api.get('/admin/tracker');
      setOverview(response.data.data || { scores: [], templates: [] });
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load tracker overview');
    }
  }

  async function loadBranches() {
    try {
      const res = await api.get('/admin/branches');
      setBranches(res.data.data || []);
    } catch (err) {}
  }

  async function loadStudentsForBranch(bId) {
    if (!bId) {
      setBranchStudents([]);
      return;
    }
    try {
      const res = await api.get('/students', { params: { branch_id: bId } });
      setBranchStudents(res.data.data || []);
    } catch (err) {
      console.error('Failed to load students for branch:', err);
    }
  }

  // Handle Branch Change in Modal (Admin)
  const handleBranchChange = (e) => {
    const bId = e.target.value;
    setTrackerBranchId(bId);
    setTrackerStudentId('');
    setAllSkills([]);
    setMilestones([]);
    setProgressNotes('');
    loadStudentsForBranch(bId);
  };

  // Handle Student Change in Modal
  const handleStudentChange = (e) => {
    const sId = e.target.value;
    setTrackerStudentId(sId);
    if (sId) {
      fetchStudentTracker(sId);
    } else {
      setAllSkills([]);
      setMilestones([]);
      setProgressNotes('');
    }
  };

  // Fetch student's tracker data from backend
  async function fetchStudentTracker(sId) {
    setLoadingStudentTracker(true);
    try {
      const res = await api.get(`/students/${sId}/tracker`, { params: { academic_year: '2024-25' } });
      const data = res.data.data || {};
      const skills = data.skills || [];
      const scores = data.term_scores || {};
      setAllSkills(skills);
      setTermScores(scores);

      // Filter skills for current category
      const currentCatSkills = skills.filter(s => (s.category || '').toLowerCase() === selectedCategory.toLowerCase());
      setMilestones(currentCatSkills);

      // Extract notes for current term and category
      const catScores = scores[selectedCategory.toLowerCase()] || [];
      const match = catScores.find(item => item.term === selectedTerm);
      setProgressNotes(match?.description || '');
    } catch (err) {
      console.error('Error loading student tracker:', err);
    } finally {
      setLoadingStudentTracker(false);
    }
  }

  // Update Category & Term view
  useEffect(() => {
    if (trackerStudentId) {
      const currentCatSkills = allSkills.filter(s => (s.category || '').toLowerCase() === selectedCategory.toLowerCase());
      setMilestones(currentCatSkills);

      const catScores = termScores[selectedCategory.toLowerCase()] || [];
      const match = catScores.find(item => item.term === selectedTerm);
      setProgressNotes(match?.description || '');
    }
  }, [selectedCategory, selectedTerm, trackerStudentId]);

  // Open Edit / Add Development Tracker Modal
  const openTrackerModal = () => {
    setIsTrackerModalOpen(true);
    setError('');
    setSuccessMessage('');
    if (user?.role === 'center_manager') {
      const bId = currentBranch?.branch_id ? String(currentBranch.branch_id) : '';
      setTrackerBranchId(bId);
      if (bId && !branchStudents.length) loadStudentsForBranch(bId);
    }
  };

  // Add a new milestone into local state
  const handleAddMilestoneSubmit = (e) => {
    e.preventDefault();
    if (!newMilestoneTitle.trim()) return;

    const newSkill = {
      category: selectedCategory,
      skill_name: newMilestoneTitle.trim(),
      percentage: Number(newMilestonePercentage),
      academic_year: '2024-25',
      is_new: true
    };

    setMilestones(prev => [...prev, newSkill]);
    setAllSkills(prev => [...prev, newSkill]);
    setNewMilestoneTitle('');
    setNewMilestonePercentage(50);
    setIsAddMilestoneOpen(false);
  };

  // Delete a milestone skill
  const handleDeleteMilestone = async (index, milestoneItem) => {
    if (milestoneItem.id) {
      try {
        await api.delete(`/students/tracker/skills/${milestoneItem.id}`);
      } catch (err) {
        console.error('Failed to delete skill:', err);
      }
    }
    setMilestones(prev => prev.filter((_, idx) => idx !== index));
    setAllSkills(prev => prev.filter(s => s !== milestoneItem));
  };

  // Save Student Development Tracker & Milestones
  const handleSaveTracker = async () => {
    if (!trackerStudentId) {
      alert('Please select a student');
      return;
    }

    setSavingTracker(true);
    try {
      // 1. Save all milestone skills for current category
      for (const m of milestones) {
        await api.post('/students/tracker/skills', {
          student_id: trackerStudentId,
          category: selectedCategory,
          skill_name: m.skill_name,
          percentage: Number(m.percentage),
          academic_year: '2024-25'
        });
      }

      // 2. Calculate average score for category
      const avgScore = milestones.length
        ? Math.round(milestones.reduce((acc, item) => acc + Number(item.percentage), 0) / milestones.length)
        : 0;

      // 3. Save term score & progress notes
      await api.post('/students/tracker/scores', {
        student_id: trackerStudentId,
        term: selectedTerm,
        academic_year: '2024-25',
        category: selectedCategory,
        score: avgScore,
        description: progressNotes ? progressNotes.trim() : null
      });

      setSuccessMessage('Development Tracker & Milestones saved successfully!');
      setIsTrackerModalOpen(false);
      loadOverview();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save development tracker');
    } finally {
      setSavingTracker(false);
    }
  };

  // Milestone Template Creation
  const createTemplate = async (event) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');
    try {
      await api.post('/admin/tracker/templates', template);
      setTemplate(emptyTemplate);
      setIsTemplateModalOpen(false);
      setSuccessMessage('Milestone template created successfully');
      loadOverview();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to create milestone template');
    }
  };

  return (
    <div>
      <PageHeader title="Development Tracker Management" subtitle="Manage milestones and compare term-wise progress" />
      {successMessage && <div className="success-message">{successMessage}</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="page-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 18, color: '#0f766e', fontWeight: 800 }}>Development Tracker & Milestones</h3>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            className="primary-button"
            onClick={openTrackerModal}
            style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', border: 'none', fontWeight: 600 }}
          >
            ✏️ Edit / Add Development Tracker
          </button>
        </div>
      </div>

      {/* ── EDIT / ADD DEVELOPMENT TRACKER MODAL ── */}
      {isTrackerModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: '#ffffff', borderRadius: 16, padding: 28,
            width: 580, maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 40px rgba(0,0,0,0.22)', border: '1px solid #e2e8f0'
          }}>
            {/* Modal Title */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, borderBottom: '1px solid #f1f5f9', paddingBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: '#1e1b4b' }}>
                Edit / Add Development Tracker
              </h3>
              <button
                type="button"
                onClick={() => setIsTrackerModalOpen(false)}
                style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: '#64748b' }}
              >
                ✕
              </button>
            </div>

            {/* 1. Branch & Student Selection */}
            <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: 20 }}>
              {user?.role === 'admin' && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Select Branch *
                  </label>
                  <select
                    value={trackerBranchId}
                    onChange={handleBranchChange}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, background: '#fff' }}
                  >
                    <option value="">-- Choose Branch --</option>
                    {branches.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Select Student *
                </label>
                <select
                  value={trackerStudentId}
                  onChange={handleStudentChange}
                  disabled={user?.role === 'admin' && !trackerBranchId}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, background: '#fff' }}
                >
                  <option value="">-- Choose Student --</option>
                  {branchStudents.map(s => (
                    <option key={s.id} value={s.id}>{s.full_name} ({s.student_id})</option>
                  ))}
                </select>
              </div>
            </div>

            {trackerStudentId && (
              <>
                {/* 2. Select Academic Term */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 13, fontWeight: 700, color: '#334155', display: 'block', marginBottom: 8 }}>
                    Select Academic Term
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, background: '#f1f5f9', padding: 4, borderRadius: 10 }}>
                    {TERMS.map(term => (
                      <button
                        key={term.id}
                        type="button"
                        onClick={() => setSelectedTerm(term.id)}
                        style={{
                          padding: '10px 0',
                          borderRadius: 8,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 14,
                          fontWeight: 600,
                          background: selectedTerm === term.id ? '#4f46e5' : 'transparent',
                          color: selectedTerm === term.id ? '#ffffff' : '#64748b',
                          boxShadow: selectedTerm === term.id ? '0 2px 8px rgba(79, 70, 229, 0.3)' : 'none',
                          transition: 'all 0.2s'
                        }}
                      >
                        {term.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 3. Select Development Area */}
                <div style={{ marginBottom: 22 }}>
                  <label style={{ fontSize: 13, fontWeight: 700, color: '#334155', display: 'block', marginBottom: 8 }}>
                    Select Development Area
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {CATEGORIES.map(cat => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setSelectedCategory(cat.id)}
                        style={{
                          padding: '8px 18px',
                          borderRadius: 20,
                          border: selectedCategory === cat.id ? 'none' : '1px solid #cbd5e1',
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: 600,
                          background: selectedCategory === cat.id ? '#6366f1' : '#ffffff',
                          color: selectedCategory === cat.id ? '#ffffff' : '#475569',
                          boxShadow: selectedCategory === cat.id ? '0 2px 8px rgba(99, 102, 241, 0.35)' : 'none',
                          transition: 'all 0.2s'
                        }}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 4. Milestones & Progress Section */}
                <div style={{ marginBottom: 22 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                      Milestones & Progress
                    </h4>
                    <button
                      type="button"
                      onClick={() => setIsAddMilestoneOpen(true)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#6366f1',
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}
                    >
                      + Add Milestone
                    </button>
                  </div>

                  {loadingStudentTracker ? (
                    <div style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>Loading milestones...</div>
                  ) : milestones.length ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {milestones.map((m, index) => (
                        <div
                          key={index}
                          style={{
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 12,
                            padding: '14px 16px'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{m.skill_name}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <span style={{ fontWeight: 800, fontSize: 14, color: '#4f46e5' }}>{m.percentage}%</span>
                              <button
                                type="button"
                                onClick={() => handleDeleteMilestone(index, m)}
                                style={{
                                  border: 'none',
                                  background: 'transparent',
                                  color: '#ef4444',
                                  cursor: 'pointer',
                                  fontSize: 16,
                                  padding: 2
                                }}
                                title="Delete Milestone"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                          {/* Progress Slider */}
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={m.percentage}
                            onChange={(e) => {
                              const val = Number(e.target.value);
                              setMilestones(prev => prev.map((item, idx) => idx === index ? { ...item, percentage: val } : item));
                            }}
                            style={{
                              width: '100%',
                              accentColor: '#6366f1',
                              cursor: 'pointer'
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      padding: 20,
                      textAlign: 'center',
                      background: '#f8fafc',
                      borderRadius: 12,
                      border: '1px dashed #cbd5e1',
                      color: '#64748b',
                      fontSize: 13
                    }}>
                      No milestones added for {selectedCategory}. Click <strong>+ Add Milestone</strong> to add one!
                    </div>
                  )}
                </div>

                {/* 5. Progress Notes & Remarks */}
                <div style={{ marginBottom: 24 }}>
                  <label style={{ fontSize: 13, fontWeight: 700, color: '#334155', display: 'block', marginBottom: 6 }}>
                    Progress Notes & Remarks
                  </label>
                  <textarea
                    rows={3}
                    value={progressNotes}
                    onChange={(e) => setProgressNotes(e.target.value)}
                    placeholder="Enter teacher notes and observations for this term..."
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: '1px solid #cbd5e1',
                      fontSize: 14,
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </>
            )}

            {/* Modal Actions */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
              <button
                type="button"
                onClick={() => setIsTrackerModalOpen(false)}
                style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: 14 }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!trackerStudentId || savingTracker}
                onClick={handleSaveTracker}
                style={{
                  padding: '9px 22px',
                  borderRadius: 8,
                  border: 'none',
                  background: trackerStudentId ? 'linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)' : '#cbd5e1',
                  color: '#fff',
                  cursor: trackerStudentId ? 'pointer' : 'not-allowed',
                  fontSize: 14,
                  fontWeight: 600,
                  boxShadow: trackerStudentId ? '0 4px 12px rgba(79, 70, 229, 0.3)' : 'none'
                }}
              >
                {savingTracker ? 'Saving Tracker...' : 'Save Tracker & Milestones'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SUB-MODAL: ADD NEW MILESTONE ── */}
      {isAddMilestoneOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100
        }}>
          <div style={{
            background: '#ffffff', borderRadius: 16, padding: 24,
            width: 380, boxShadow: '0 20px 40px rgba(0,0,0,0.25)', border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ marginTop: 0, fontSize: 17, fontWeight: 700, color: '#1e1b4b', marginBottom: 16 }}>
              Add New Milestone
            </h3>

            <form onSubmit={handleAddMilestoneSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>
                  Milestone Title *
                </label>
                <input
                  type="text"
                  value={newMilestoneTitle}
                  onChange={(e) => setNewMilestoneTitle(e.target.value)}
                  placeholder="e.g. Letter Recognition, Sports"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, boxSizing: 'border-box' }}
                  autoFocus
                  required
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Initial Progress</label>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#4f46e5' }}>{newMilestonePercentage}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={newMilestonePercentage}
                  onChange={(e) => setNewMilestonePercentage(Number(e.target.value))}
                  style={{ width: '100%', accentColor: '#6366f1', cursor: 'pointer' }}
                />
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setIsAddMilestoneOpen(false)}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: 13 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                >
                  Add Milestone
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── CREATE MILESTONE TEMPLATE MODAL ── */}
      <Modal isOpen={isTemplateModalOpen} onClose={() => setIsTemplateModalOpen(false)} title="Create Milestone Template">
        <form onSubmit={createTemplate}>
          <label>
            Milestone title
            <input placeholder="Milestone title" value={template.title} onChange={(e) => setTemplate((p) => ({ ...p, title: e.target.value }))} required />
          </label>
          <label>
            Category
            <select value={template.category} onChange={(e) => setTemplate((p) => ({ ...p, category: e.target.value }))}>
              <option value="cognitive">Cognitive</option>
              <option value="emotional">Emotional</option>
              <option value="social">Social</option>
              <option value="motor">Motor Skills</option>
            </select>
          </label>
          <label>
            Age group
            <input placeholder="Age group" value={template.age_group} onChange={(e) => setTemplate((p) => ({ ...p, age_group: e.target.value }))} />
          </label>
          <label>
            Description
            <textarea placeholder="Description" value={template.description} onChange={(e) => setTemplate((p) => ({ ...p, description: e.target.value }))} />
          </label>
          <div className="modal-actions">
            <button type="button" className="secondary-button" onClick={() => setIsTemplateModalOpen(false)}>Cancel</button>
            <button type="submit" className="primary-button">Create Template</button>
          </div>
        </form>
      </Modal>

      {/* ── OVERVIEW TABLES ── */}
      <section className="split-grid">
        <div>
          <h3>Milestone Templates</h3>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Age</th>
                </tr>
              </thead>
              <tbody>
                {overview.templates.map((item) => (
                  <tr key={item.id}>
                    <td>{item.title}</td>
                    <td>{item.category}</td>
                    <td>{item.age_group || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h3>Term Progress</h3>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Term</th>
                  <th>Category</th>
                  <th>Average</th>
                </tr>
              </thead>
              <tbody>
                {overview.scores.map((score, index) => (
                  <tr key={`${score.student_id}-${score.term}-${score.category}-${index}`}>
                    <td>{score.student_name}</td>
                    <td>{score.term} {score.academic_year}</td>
                    <td>{score.category}</td>
                    <td>{Math.round(score.average_score || 0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

